import { adaptiveQueueProcessor, type QueueProcessingStats } from './adaptive-queue-processor';
import { logger } from '@/utils/logger';
import { localLogger } from '@/lib/local-logger';

export type QueueTriggerReason =
  | 'recording_saved'           // After recording completion (use-quick-record)
  | 'connectivity_restored'     // When coming back online
  | 'periodic_check'            // Periodic background check
  | 'app_focus'                 // App regains focus
  | 'window_focus'              // Window regains focus
  | 'manual_trigger'            // User clicked process button
  | 'recovery_attempt'          // Transcription recovery
  | 'progress_check';           // Progress tracking

export interface QueueCoordinatorStats extends QueueProcessingStats {
  reason: QueueTriggerReason;
  timestamp: number;
  queueWasLocked: boolean;
}

/**
 * Triggers that fire on their own schedule (timers, focus, polling).
 *
 * When a run finishes and the queue was empty, a follow-up run for one of these
 * would find nothing either, so it is dropped. Every other reason implies the
 * caller knows about work that may not have been visible to the finished run.
 */
const OPPORTUNISTIC_REASONS: ReadonlySet<QueueTriggerReason> = new Set([
  'periodic_check',
  'app_focus',
  'window_focus',
  'progress_check'
]);

interface PendingRequest {
  userId: string;
  reason: QueueTriggerReason;
  timestamp: number;
}

/**
 * QueueCoordinator: Single entry point for all queue processing
 *
 * Prevents:
 * - Concurrent processing (single-flight: callers share the in-flight run)
 * - Duplicate requests (triggers arriving during a run collapse into one follow-up)
 * - Excessive API calls (minimum interval between runs)
 *
 * Ensures:
 * - All queue processing goes through one place
 * - Consistent logging and error handling
 * - Coordinated processing across all components
 */
export class QueueCoordinator {
  private static instance: QueueCoordinator;
  private activeRun: Promise<QueueCoordinatorStats | null> | null = null;
  private lastProcessingTime = 0;
  private minIntervalMs = 1000; // Minimum 1 second between processing attempts
  private pending: PendingRequest | null = null;
  private pendingTimer: ReturnType<typeof setTimeout> | null = null;
  private lastStats: QueueCoordinatorStats | null = null;
  /** true/false after a completed run, null when the last run errored out */
  private lastRunFoundEmptyQueue: boolean | null = null;

  private constructor() {}

  public static getInstance(): QueueCoordinator {
    if (!QueueCoordinator.instance) {
      QueueCoordinator.instance = new QueueCoordinator();
    }
    return QueueCoordinator.instance;
  }

  /**
   * Process the queue with coordination
   *
   * Concurrent callers do not start a second run: they await the one already in
   * flight and get its stats. Their trigger is remembered and collapsed into a
   * single follow-up run so work saved mid-run is still picked up.
   *
   * @param userId - User ID to process queue for
   * @param reason - Why queue processing was triggered
   * @returns Processing statistics, or null if the run was deferred
   */
  async processQueue(
    userId: string,
    reason: QueueTriggerReason = 'manual_trigger'
  ): Promise<QueueCoordinatorStats | null> {
    const now = Date.now();

    // A run is already in flight: share it rather than queueing a second one.
    if (this.activeRun) {
      logger.debug(`Queue busy, coalescing request: ${reason}`);
      this.pending = { userId, reason, timestamp: now };
      return this.activeRun;
    }

    // Rate limiting: don't start runs back to back, defer instead of dropping.
    const timeSinceLastProcessing = now - this.lastProcessingTime;
    if (timeSinceLastProcessing < this.minIntervalMs) {
      logger.debug(`Queue rate limited, deferring request: ${reason}`, {
        waitMs: this.minIntervalMs - timeSinceLastProcessing
      });
      this.pending = { userId, reason, timestamp: now };
      this.schedulePending();
      return null;
    }

    logger.info(`🚀 Starting queue processing (reason: ${reason})`, {
      timeSinceLastProcessing
    });

    this.lastProcessingTime = now;
    this.activeRun = this.runQueue(userId, reason, now);

    try {
      return await this.activeRun;
    } finally {
      this.activeRun = null;
      this.schedulePending();
    }
  }

  /**
   * Run the queue once. Never throws: failures resolve to null so that shared
   * awaiters and the follow-up scheduler behave the same way on every path.
   */
  private async runQueue(
    userId: string,
    reason: QueueTriggerReason,
    startedAt: number
  ): Promise<QueueCoordinatorStats | null> {
    try {
      const stats = await adaptiveQueueProcessor.processQueue(userId);

      const coordinatorStats: QueueCoordinatorStats = {
        ...stats,
        reason,
        timestamp: startedAt,
        queueWasLocked: false
      };

      this.lastStats = coordinatorStats;
      this.lastRunFoundEmptyQueue = stats.queueSize === 0 && stats.totalProcessed === 0;

      if (stats.successful > 0) {
        logger.info(`✅ Queue processing completed: ${stats.successful} successful, ${stats.failed} failed`);
      } else if (stats.failed > 0) {
        logger.warn(`⚠️ Queue processing completed with failures: ${stats.failed} failed`);
      } else {
        logger.debug(`ℹ️ Queue processing completed: no recordings to process`);
      }

      return coordinatorStats;
    } catch (error) {
      logger.error('Queue processing failed:', error);
      localLogger.captureError(error as Error, {
        component: 'queue_coordinator',
        metadata: { reason }
      });
      // Unknown queue state - let any follow-up run rather than dropping it.
      this.lastRunFoundEmptyQueue = null;
      return null;
    }
  }

  /**
   * Schedule the single collapsed follow-up run, if one is still warranted.
   *
   * Only ever one timer is outstanding, and it starts after the minimum
   * interval has elapsed, so the follow-up can never be rate limited into
   * re-queueing itself.
   */
  private schedulePending(): void {
    if (!this.pending || this.pendingTimer || this.activeRun) return;

    // The queue was empty on the last run, so a background trigger that arrived
    // during it has nothing to do.
    if (this.lastRunFoundEmptyQueue === true && OPPORTUNISTIC_REASONS.has(this.pending.reason)) {
      logger.debug(`Dropping redundant queue request: ${this.pending.reason} (queue empty)`);
      this.pending = null;
      return;
    }

    const delay = Math.max(0, this.minIntervalMs - (Date.now() - this.lastProcessingTime));

    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;

      const next = this.pending;
      this.pending = null;
      if (!next) return;

      logger.debug(`Running coalesced queue request: ${next.reason}`);
      this.processQueue(next.userId, next.reason).catch(err => {
        logger.error('Failed to process coalesced queue request:', err);
      });
    }, delay);
  }

  /**
   * Get the last processing statistics
   */
  getLastStats(): QueueCoordinatorStats | null {
    return this.lastStats;
  }

  /**
   * Check if queue is currently processing
   */
  isQueueProcessing(): boolean {
    return this.activeRun !== null;
  }

  /**
   * Number of collapsed requests waiting for a follow-up run (0 or 1)
   */
  getQueueDepth(): number {
    return this.pending ? 1 : 0;
  }

  /**
   * Get time since last processing
   */
  getTimeSinceLastProcessing(): number {
    return Date.now() - this.lastProcessingTime;
  }
}

export const queueCoordinator = QueueCoordinator.getInstance();
